import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../../components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";

const PRODUCT_LABELS = { gold: "Gold", gold_pro: "Gold Pro", muscle_school: "Muscle School", book: "Book", mods: "MODS", mods_pro: "MODS Pro" };

function CustomerDetail({ accountId, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-customer", accountId],
    queryFn: async () => (await api.get(`/admin/customers/${accountId}`)).data,
    enabled: !!accountId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-customer", accountId] });
    qc.invalidateQueries({ queryKey: ["admin-customers"] });
  };

  const act = async (action, body) => {
    try {
      await api.post(`/admin/customers/${accountId}/${action}`, body);
      toast.success("Done.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Action failed.");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/admin/customers/${accountId}/notes`, { note: noteText.trim() });
      setNoteText("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note.");
    } finally {
      setAddingNote(false);
    }
  };

  const account = data?.account;

  return (
    <Dialog open={!!accountId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-surface border-white/10 text-white" data-testid="admin-customer-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="admin-customer-detail-email" className="pr-8 truncate">{account?.email || "Loading…"}</DialogTitle>
        </DialogHeader>
        {isLoading && <p className="text-white/60 text-sm">Loading…</p>}
        {account && (
          <div className="space-y-6 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge data-testid="admin-customer-status-badge" className={account.status === "suspended" ? "bg-red-500/15 text-red-300 border-red-500/40" : "bg-green-500/10 text-green-300 border-green-500/30"}>
                {account.status === "suspended" ? "Suspended" : "Active"}
              </Badge>
              <Badge variant="outline">{account.email_verified ? "Verified" : "Unverified"}</Badge>
              <Badge variant="outline">{data.active_session_count} active session(s)</Badge>
            </div>
            <p className="text-white/50 text-xs">Registered {new Date(account.created_at).toLocaleString()}</p>

            <div>
              <p className="text-xs uppercase tracking-wide text-white/50 mb-2">Entitlements</p>
              {data.entitlements?.length ? (
                <div className="space-y-1">
                  {data.entitlements.map((e) => (
                    <div key={e.id} data-testid={`admin-customer-entitlement-${e.id}`} className="flex justify-between border-b border-white/5 py-1 text-xs">
                      <span>{PRODUCT_LABELS[e.product_type] || e.product_type}</span>
                      <span className="text-white/60">{e.status}{e.grace_until ? ` · grace until ${new Date(e.grace_until).toLocaleDateString()}` : ""}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-white/40 text-xs">None.</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-white/50 mb-2">Orders & Billing</p>
              {(data.orders?.length || data.transactions?.length) ? (
                <div className="space-y-1">
                  {data.orders?.map((o) => (
                    <div key={o.id} className="flex justify-between border-b border-white/5 py-1 text-xs">
                      <span>{o.items?.map((i) => i.title).join(", ")}</span>
                      <span className="text-white/60">${(o.amount_subtotal_cents / 100).toFixed(2)} · {o.status}</span>
                    </div>
                  ))}
                  {data.transactions?.map((t) => (
                    <div key={t.id} className="flex justify-between border-b border-white/5 py-1 text-xs">
                      <span>{PRODUCT_LABELS[t.entitlement_product_type] || t.entitlement_product_type}</span>
                      <span className="text-white/60">${(t.amount_cents / 100).toFixed(2)} · {t.payment_status}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-white/40 text-xs">None.</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-white/50 mb-2">Recent Security Events</p>
              {data.recent_events?.length ? (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {data.recent_events.map((ev) => (
                    <div key={ev.id} className="flex justify-between text-xs text-white/60">
                      <span className="capitalize">{ev.event_type.replace(/_/g, " ")}</span>
                      <span>{ev.outcome} · {new Date(ev.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-white/40 text-xs">None yet.</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-white/50 mb-2">Support Notes</p>
              {data.notes?.length ? (
                <div className="space-y-2 max-h-40 overflow-y-auto mb-2" data-testid="admin-customer-notes-list">
                  {data.notes.map((n) => (
                    <div key={n.id} className="text-xs border-b border-white/5 pb-1.5" data-testid={`admin-customer-note-${n.id}`}>
                      <p className="text-white/80">{n.note}</p>
                      <p className="text-white/40">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-white/40 text-xs mb-2">No support notes yet.</p>}
              <div className="flex gap-2">
                <Textarea data-testid="admin-customer-note-input" placeholder="Add a support note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} className="text-xs" />
                <button data-testid="admin-customer-note-add-btn" onClick={addNote} disabled={addingNote || !noteText.trim()}
                  className="text-xs uppercase tracking-wide border border-white/20 px-3 hover:border-white/50 disabled:opacity-40 shrink-0">
                  {addingNote ? "Saving…" : "Add"}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
              {account.status === "suspended" ? (
                <button data-testid="admin-customer-restore-btn" onClick={() => act("restore")} className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50">
                  Restore Account
                </button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="admin-customer-suspend-btn" className="text-xs uppercase tracking-wide border border-red-400/40 text-red-300 px-4 py-2 hover:border-red-400">
                      Suspend Account
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-surface border-white/10 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Suspend this customer?</AlertDialogTitle>
                      <AlertDialogDescription className="text-white/60">
                        They will be signed out of every active session immediately and blocked from signing back in until restored. A reason is required for the audit record.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <Textarea data-testid="admin-customer-suspend-reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="admin-customer-suspend-confirm" disabled={!reason.trim()} onClick={() => { act("suspend", { reason }); setReason(""); }}>
                        Suspend
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <button data-testid="admin-customer-reset-password-btn" onClick={() => act("reset-password")} className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50">
                Send Password Reset
              </button>
              <button data-testid="admin-customer-revoke-sessions-btn" onClick={() => act("revoke-sessions")} className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50">
                Revoke All Sessions
              </button>
            </div>
          </div>
        )}
        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCustomers() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [verified, setVerified] = useState("all");
  const [product, setProduct] = useState("all");
  const [dataOrigin, setDataOrigin] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-customers", q, status, verified, product, dataOrigin, page],
    queryFn: async () => (await api.get("/admin/customers", {
      params: {
        q, page, page_size: 25,
        status: status === "all" ? "" : status,
        verified: verified === "all" ? "" : verified,
        product: product === "all" ? "" : product,
        data_origin: dataOrigin === "all" ? "" : dataOrigin,
      },
    })).data,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/customers/export.csv", {
        params: { q, status: status === "all" ? "" : status, verified: verified === "all" ? "" : verified, product: product === "all" ? "" : product, data_origin: dataOrigin === "all" ? "" : dataOrigin },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "endure_beyond_customers_export.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch {
      toast.error("Could not export customers.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-display text-display-xs">Customer Directory</h1>
        <button data-testid="admin-customers-export-btn" onClick={exportCsv} disabled={exporting}
          className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50 disabled:opacity-40">
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <input
          data-testid="admin-customers-search-input"
          placeholder="Search by email…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          className="bg-transparent border border-white/20 px-3 py-2 text-sm min-w-[220px]"
        />
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-customers-status-filter" className="w-36 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent>
        </Select>
        <Select value={verified} onValueChange={(v) => { setVerified(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-customers-verified-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any verification</SelectItem><SelectItem value="yes">Verified</SelectItem><SelectItem value="no">Unverified</SelectItem></SelectContent>
        </Select>
        <Select value={product} onValueChange={(v) => { setProduct(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-customers-product-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any product</SelectItem>
            <SelectItem value="gold">Gold / Gold Pro</SelectItem>
            <SelectItem value="school">Muscle School</SelectItem>
            <SelectItem value="book">Books</SelectItem>
            <SelectItem value="mods">MODS</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dataOrigin} onValueChange={(v) => { setDataOrigin(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-customers-origin-filter" className="w-40 bg-transparent border-white/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Genuine + test</SelectItem>
            <SelectItem value="genuine">Genuine only</SelectItem>
            <SelectItem value="test">Test/dev only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <p className="text-white/60 text-sm">Loading…</p>}
      <div className="divide-y divide-white/10 border-t border-b border-white/10" data-testid="admin-customers-table">
        {data?.items?.map((c) => (
          <button
            key={c.id}
            data-testid={`admin-customer-row-${c.id}`}
            onClick={() => setSelected(c.id)}
            className="w-full flex items-center justify-between gap-3 py-3 text-left text-sm hover:bg-white/[0.03] transition-colors flex-wrap"
          >
            <span>{c.email}</span>
            <span className="flex items-center gap-2 flex-wrap">
              {c.is_test && <Badge className="bg-white/5 text-white/40 border-white/20" data-testid={`admin-customer-test-badge-${c.id}`}>Test/Dev</Badge>}
              {c.products?.map((p) => <Badge key={p} variant="outline">{PRODUCT_LABELS[p] || p}</Badge>)}
              <Badge className={c.status === "suspended" ? "bg-red-500/15 text-red-300 border-red-500/40" : "bg-green-500/10 text-green-300 border-green-500/30"}>{c.status === "suspended" ? "Suspended" : "Active"}</Badge>
              <Badge variant="outline">{c.email_verified ? "Verified" : "Unverified"}</Badge>
            </span>
          </button>
        ))}
        {data && data.items.length === 0 && <p data-testid="admin-customers-empty" className="text-white/50 text-sm py-6">No customers match these filters.</p>}
      </div>

      {data && totalPages > 1 && (
        <div className="flex items-center gap-4 mt-6 text-xs text-white/60">
          <button data-testid="admin-customers-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="underline disabled:opacity-30">Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button data-testid="admin-customers-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="underline disabled:opacity-30">Next</button>
        </div>
      )}

      {selected && <CustomerDetail accountId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
